<script setup lang="ts" generic="T extends { id: number; latitude: number; longitude: number }">
import { computed, nextTick, ref, watch } from 'vue';
import { AdvancedMarker,GoogleMap } from 'vue3-google-map';

const props = defineProps<{
  initialItems: T[];
  ariaLabel?: string;
}>();

const emit = defineEmits<{
  'marker-click': [item: T];
  'map-loaded': [void];
}>();

// Map references and state
const googleMap = ref<InstanceType<typeof GoogleMap>>();
const mapRef = ref<google.maps.Map>();
const mapIsLoaded = ref(false);
const mapZoom = ref(7);
const items = ref<T[]>(props.initialItems);

// Calculate the center of all regions
const mapCenter = computed(() => {
  if (!items.value.length) {
    return { lat: 0, lng: 0 };
  }

  const validItems = items.value.filter(
    region => typeof region.latitude === 'number' && typeof region.longitude === 'number'
  );
  
  if (!validItems.length) {
    return { lat: 0, lng: 0 };
  }

  const totalLat = validItems.reduce((sum, region) => sum + region.latitude, 0);
  const totalLng = validItems.reduce((sum, region) => sum + region.longitude, 0);
  
  return {
    lat: totalLat / validItems.length,
    lng: totalLng / validItems.length
  };
});

// Set appropriate marker options based on region status
function getMarkerOptions(): google.maps.marker.AdvancedMarkerElementOptions {
  return {
    position: { lat: 0, lng: 0 },
    title: 'Placeholder',
    gmpClickable: true,
  };
}

function handleMarkerClick({ domEvent }: { domEvent: PointerEvent }, item: T) {
  const { target } = domEvent;
  const marker = (target as SVGElement).closest('gmp-advanced-marker') as HTMLButtonElement;
  marker.setAttribute('aria-expanded', 'true');
  emit('marker-click', item);
}

function fitBounds() {
  if (!mapRef.value || !items.value.length) return;
  
  const map = mapRef.value;
  const bounds = new google.maps.LatLngBounds();
  const validPositions: google.maps.LatLngLiteral[] = [];
  
  items.value.forEach(item => {
    if (typeof item.latitude === 'number' && typeof item.longitude === 'number') {
      const position = { lat: item.latitude, lng: item.longitude };
      bounds.extend(position);
      validPositions.push(position);
    }
  });
  
  if (!bounds.isEmpty()) {
    const padding = { top: 50, right: 50, bottom: 50, left: 50 };
    mapRef.value.fitBounds(bounds, padding);

    google.maps.event.clearListeners(map, 'idle');
    google.maps.event.addListenerOnce(map, 'idle', () => {
      const currentZoom = map.getZoom();
      if (currentZoom !== undefined) {
        const MIN_ZOOM_LEVEL = 3;
        const MAX_ZOOM_LEVEL = 10;
        if (currentZoom < MIN_ZOOM_LEVEL) {
          mapZoom.value = MIN_ZOOM_LEVEL;
        } else if (currentZoom > MAX_ZOOM_LEVEL) {
          mapZoom.value = MAX_ZOOM_LEVEL;
        } else {
          mapZoom.value = currentZoom;
        }
      }
    });
  }
}

// Watch for changes in items
watch(() => items.value, () => {
  if (mapIsLoaded.value) {
    nextTick(() => {
      fitBounds();
    });
  }
}, { deep: true });

// Update items when initialItems prop changes
watch(() => props.initialItems, (newItems) => {
  items.value = newItems;
}, { deep: true });

watch(() => googleMap.value?.ready, (isReady) => {
  if (isReady) {
    mapIsLoaded.value = true;
    mapRef.value = googleMap.value?.map;
    emit('map-loaded');
    nextTick(() => {
      fitBounds();
    });
  }
}, {
  immediate: true, deep: true});
</script>

<template>
  <div class="globalGoogleMap">
    <GoogleMap ref="googleMap"
               api-key="AIzaSyA5oNLf2Z4LkCZgd58Hei7FehuAo1vst2A"
               map-id="r4a75aac462151035"
               class="globalGoogleMap-container"
               :center="mapCenter"
               :zoom="mapZoom"
               :aria-label="ariaLabel">
      <slot name="markers">
        <template v-for="item in initialItems"
                  :key="item.id">
          <slot :item="item"
                name="marker">
            <AdvancedMarker :key="item.id"
                            :options="getMarkerOptions()"
                            @click="handleMarkerClick($event, item)">
            </AdvancedMarker>
          </slot>
        </template>
      </slot>
    </GoogleMap>
  </div>
</template>

<style lang="scss">
.globalGoogleMap {
  width: 100%;
  position: relative;
  border-radius: var(--radius-md);
  border: var(--contentSizeBorderWidth, 2px) solid var(--contentColorBorder, var(--gray-200));
  overflow:hidden;

  [aria-roledescription="map"] {
    border: var(--contentSizeBorderWidth) solid var(--contentColorBorder);
    border-radius: 8px;
  }
}

.globalGoogleMap-container {
  width: 100%;
  height: 500px;
  border-radius: var(--radius-md);
}

[aria-label^="Cluster"] {
  svg {
    width: 64px;
    height: auto;

    fill: var(--primary-950);

    circle:first-of-type {
      opacity: 0.8;
    }

    circle:last-of-type {
      opacity: 0.6;
    }
  }
}

/* Styles des marqueurs */
.hg-marker {
  position: relative;
  padding: 8px 8px;
  border: 1px solid var(--primaryColor);

  background-color: var(--primary-950);
  border-radius: 8px;

  

  &::after {
    content: "";
    position: absolute;
    z-index: 2;
    top: 100%;
    left: 50%;
    width: 0;
    height: 0;
    border-right: 6px solid transparent;
    border-left: 6px solid transparent;
    border-top: 6px solid var(--primaryColor);
    translate: -50% 0;
  }

  &-pinSvg {
    width: 24px;
    height: auto;
  }

  &-infoWindow {
    display: none;
    position: absolute;
    z-index: 4;
    bottom: 100%;
    left: 50%;
    width: clamp(15rem, 11.0526rem + 19.7368vw, 30rem);
    padding: 8px;
    border: 1px solid currentColor;
    
    background-image: var(--linearGrdLightGray);
    backdrop-filter: blur(4px);
    border-radius: 8px;
    translate: -60% -8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);

    font-size: var(--fs16);

    h3 {
      margin-bottom: 0;
    }
  }

  &-infoTitle {
    padding-bottom: 4px;
    border-bottom: 1px solid currentColor;

    font-size: var(--fs24-20);
    font-family: 'Playfair Display';
  }

  &-infoContent {
    margin-top: 8px;
  }

  &-descriptionBlock {
    display: flex;
    align-items: first baseline;
    justify-content: space-between;
    gap: 1rem;
  }

  &-descriptionTitle {
    font-weight: 500;
  }
}

/* Marqueur de prix en forme de pilule */
.hg-price-marker {
  position: relative;
  cursor: pointer;
  
  &-content {
    padding: 8px 16px;
    background: white;
    border-radius: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    font-size: 14px;
    font-weight: 600;
    color: #333;
    white-space: nowrap;
    border: 1px solid rgba(0, 0, 0, 0.1);
    transition: all 0.2s ease;
    
    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
      transform: translateY(-1px);
    }
  }
  
  // Fenêtre d'info pour le marqueur de prix
  .hg-marker-infoWindow {
    display: none;
    position: absolute;
    z-index: 4;
    bottom: 100%;
    left: 50%;
    width: clamp(15rem, 11.0526rem + 19.7368vw, 30rem);
    padding: 8px;
    border: 1px solid currentColor;
    margin-bottom: 8px;
    
    background-image: var(--linearGrdLightGray);
    backdrop-filter: blur(4px);
    border-radius: 8px;
    translate: -50% 0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);

    font-size: var(--fs16);

    h3 {
      margin-bottom: 0;
    }
  }
  
  &:hover .hg-marker-infoWindow {
    display: block;
  }
}

/* Boutons superflus de la carte Google */
.gm-style-mtc-bbw {
  display: none;
}



/* États */
gmp-advanced-marker {
  &:is(:focus-visible, :hover) {
    z-index: 1;
    .hg-marker-infoWindow {
      display: block;
    }
  }

  &:has([data-hovered-item="true"]) {
    scale: 1.05;
  }
}
</style>
